/* eslint-disable react/no-array-index-key */
import React from 'react'
import { connect } from 'react-redux'
import { ApplicationState } from '../../store'
import { selectDate, selectAvailableTime } from '../../store/availableTimes/actions'
import { AvailableTime, AvailableWeek } from '../../store/availableTimes/types'
import styled from '../../utils/styled'
import { ContinueButton, ContinueButtonText } from '../../styles/components/buttons'
import { Box } from '../layout/Box'
import { Typography } from '../layout/Typography'

interface PropsFromState {
  data: AvailableWeek
  selectedDate: Date
  selectedTime?: AvailableTime
}

interface PropsFromDispatch {
  selectDate: typeof selectDate
  selectAvailableTime: typeof selectAvailableTime
}

interface OwnProps {
  getNextWeekAvailableTimes: (newSelectedWeek: 'prev' | 'next') => void
}

type AllProps = PropsFromState & PropsFromDispatch & OwnProps

const getDateWeekday = (dateIsoString: string) => new Date(dateIsoString).toLocaleDateString('de-DE', { weekday: 'short' })

const getDateDay = (dateIsoString: string) => new Date(dateIsoString).toLocaleDateString('de-DE', { day: 'numeric' })

export const formatAvailableTime = (availableTime: string) => {
  return availableTime
    .split(':')
    .slice(0, 2)
    .join(':')
}

const AvailableTimesComponent: React.FC<AllProps> = ({
  data: availableTimesData,
  selectedDate,
  selectedTime,
  selectDate: dispatchSelectDate,
  selectAvailableTime: dispatchSelectAvailableTime,
  getNextWeekAvailableTimes
}) => {
  if (Object.keys(availableTimesData).length === 0) return null

  const getAvailableTimesFromSelectedDate = () => {
    const selectedDateIsoString = selectedDate.toISOString()

    const selectedAvailableTimesEntries = Object.entries(availableTimesData).find(([availableDate, availableTimes]) => {
      const availableDateIsoString = new Date(availableDate).toISOString()
      return availableDateIsoString === selectedDateIsoString && availableTimes.length > 0
    })

    if (!selectedAvailableTimesEntries) return []

    return selectedAvailableTimesEntries[1] || []
  }

  const getMonthOfSelectedDate = () => {
    const selectedDateIsoString = selectedDate.toISOString()
    return new Date(selectedDateIsoString).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
  }

  const isDateDisabled = (dateIsoString: string) => availableTimesData[dateIsoString].length === 0

  const isDateActive = (dateIsoString: string) => {
    const selectedDateIsoString = selectedDate.toISOString()
    const availableDateIsoString = new Date(dateIsoString).toISOString()
    return selectedDateIsoString === availableDateIsoString
  }

  const handleSelectDate = (dateIsoString: string) => {
    if (!isDateDisabled(dateIsoString)) dispatchSelectDate(new Date(dateIsoString))
  }

  return (
    <Box padding="0 0 1rem">
      <Typography variant="h4" margin="0 0 1rem">
        Datum und Uhrzeit auswählen
      </Typography>
      <Box flex spaceBetween centerVertical margin="0 0 1rem">
        <Typography variant="bold">{getMonthOfSelectedDate()}</Typography>
        <WeekNavigationArrowContainer>
          <WeekNavigationArrow onClick={() => getNextWeekAvailableTimes('prev')}>{'<'}</WeekNavigationArrow>
          <WeekNavigationArrow onClick={() => getNextWeekAvailableTimes('next')}>{'>'}</WeekNavigationArrow>
        </WeekNavigationArrowContainer>
      </Box>
      <WeekContainer>
        {Object.keys(availableTimesData).map((availableDate, dateIndex) => (
          <DayContainer key={availableDate}>
            <React.Fragment key={`available-date-${dateIndex}`}>
              <WeekdayItem>{getDateWeekday(availableDate)}</WeekdayItem>

              <DateItem
                active={isDateActive(availableDate)}
                disabled={isDateDisabled(availableDate)}
                onClick={() => handleSelectDate(availableDate)}
              >
                {getDateDay(availableDate)}
              </DateItem>
            </React.Fragment>
          </DayContainer>
        ))}
      </WeekContainer>
      <AvailableTimeContainer>
        {getAvailableTimesFromSelectedDate().map((availableTime, timeIndex) => (
          <AvailableTimeItem
            active={selectedTime && selectedTime.start === availableTime.start}
            onClick={() => dispatchSelectAvailableTime(availableTime)}
            key={`available-time-${timeIndex}`}
          >
            {formatAvailableTime(availableTime.start)}
          </AvailableTimeItem>
        ))}
      </AvailableTimeContainer>
      {selectedTime && (
        <ContinueButton to="/booking-confirmation">
          <ContinueButtonText>Fortfahren</ContinueButtonText>
        </ContinueButton>
      )}
    </Box>
  )
}

const WeekContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const DayContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const WeekdayItem = styled.span`
  color: ${props => props.theme.colors.body2};
  text-align: center;
`

const DateItem = styled.button<{ active?: boolean; disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  text-align: center;
  height: 2.5rem;
  width: 2.5rem;
  color: ${({ theme, active, disabled }) => {
    if (disabled) return theme.colors.body2
    if (active) return theme.colors.white
    return theme.colors.body
  }};
  background-color: ${({ theme, active }) => (active ? theme.colors.black : theme.colors.background)};
  border-radius: 1.5rem;
  border: none;
`

const WeekNavigationArrowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
`

const WeekNavigationArrow = styled.span`
  cursor: pointer;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.body};
  border: none;
  padding: 0.75rem;
`

const AvailableTimeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  padding-top: 1rem;
`

const AvailableTimeItem = styled.button<{ active?: boolean }>`
  cursor: pointer;
  text-align: center;
  flex-basis: calc(33.33% - 0.3rem - 1px);
  margin-bottom: 0.5rem;
  margin-right: 0.3rem;
  background-color: ${({ theme, active }) => (active ? theme.colors.black : theme.colors.accent)};
  color: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.body)};
  border-radius: 1rem;
  padding: 0.75rem;
  border: ${({ theme }) => `1px solid ${theme.colors.borders}`};
`

const mapStateToProps = ({ availableTimes }: ApplicationState) => ({
  data: availableTimes.data,
  selectedDate: availableTimes.selectedDate,
  selectedTime: availableTimes.selectedTime
})

const mapDispatchToProps = {
  selectDate,
  selectAvailableTime
}

export const AvailableTimes = connect(mapStateToProps, mapDispatchToProps)(AvailableTimesComponent)
